import {
	AuthCard,
	AuthCardBody,
	AuthCardHeader
} from '@repo/ui/modules/auth/components/auth-card'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { LucideProps } from 'lucide-react'
import {
	ChevronRight,
	KeySquare,
	Mail,
	ShieldCheck,
	Smartphone
} from 'lucide-react'
import type { ComponentType } from 'react'
import { useTranslation } from '@/lib/i18n'
import { adminSessionQueryOptions } from '@/lib/query-options'

export const Route = createFileRoute('/(auth)/two-factor/methods')({
	component: AdminTwoFactorMethods
})

function AdminTwoFactorMethods() {
	const { t } = useTranslation()

	// The layout guard ensured a pending challenge; the null case only occurs
	// for an instant after a successful verify, while the navigation away is
	// still in flight.
	const { data: session } = useSuspenseQuery(adminSessionQueryOptions())
	const mfa = session.sessionType === 'mfa_pending' ? session.mfa : null

	if (!mfa) {
		return null
	}

	const available = new Set(mfa.methods.map((method) => method.type))

	return (
		<AuthCard>
			<AuthCardHeader
				description={t('auth:two_factor.methods.description')}
				icon={<ShieldCheck className='size-8' />}
				title={t('auth:two_factor.methods.title')}
			/>
			<AuthCardBody>
				<div className='flex flex-col gap-2'>
					{available.has('totp') && (
						<MethodItem
							description={t(
								'auth:two_factor.methods.totp_description'
							)}
							icon={Smartphone}
							title={t('auth:two_factor.methods.totp_title')}
							to='/two-factor'
						/>
					)}
					{available.has('email_otp') && (
						<MethodItem
							description={t(
								'auth:two_factor.methods.email_description'
							)}
							icon={Mail}
							title={t('auth:two_factor.methods.email_title')}
							to='/two-factor/email'
						/>
					)}
					{available.has('recovery_code') && (
						<MethodItem
							description={t(
								'auth:two_factor.methods.recovery_description'
							)}
							icon={KeySquare}
							title={t('auth:two_factor.methods.recovery_title')}
							to='/two-factor/recovery'
						/>
					)}
				</div>
			</AuthCardBody>
		</AuthCard>
	)
}

function MethodItem(props: {
	icon: ComponentType<LucideProps>
	title: string
	description: string
	to: '/two-factor' | '/two-factor/email' | '/two-factor/recovery'
}) {
	return (
		<Link
			className='flex items-center gap-3 rounded-lg border p-4 transition-colors hover:bg-accent'
			search={(previous) => previous}
			to={props.to}
		>
			<props.icon className='size-5 text-muted-foreground' />
			<div className='flex-1'>
				<div className='font-medium text-sm'>{props.title}</div>
				<div className='text-muted-foreground text-sm'>
					{props.description}
				</div>
			</div>
			<ChevronRight className='size-4 text-muted-foreground' />
		</Link>
	)
}
